'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CaseStudy {
  id: string;
  title: string;
  client: string;
  industry: string;
  region: string;
  service: string;
  result: string;
  image: string;
  alt: string;
}

const allCaseStudies: CaseStudy[] = [
{
  id: 'case_1',
  title: 'Tech Manufacturer Exhibition Success',
  client: 'European Tech Company',
  industry: 'Technology',
  region: 'EMEA',
  service: 'Exhibition Sales',
  result: '47 qualified leads, 8 deals closed ($1.2M)',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b449fd90-1766459781422.png",
  alt: 'Technology exhibition booth with interactive displays'
},
{
  id: 'case_2',
  title: 'Investment Firm Market Entry',
  client: 'Middle Eastern Investment Firm',
  industry: 'Finance',
  region: 'CIS',
  service: 'Market Entry',
  result: '12 distributor partnerships, 3 joint ventures',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b2d3b379-1767435209026.png",
  alt: 'Business professionals shaking hands at trade show'
},
{
  id: 'case_3',
  title: 'National Pavilion Optimization',
  client: 'Government Trade Agency',
  industry: 'Government',
  region: 'EMEA',
  service: 'Representation',
  result: '+340% exhibitor ROI increase',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_110c401a4-1770066494495.png",
  alt: 'National pavilion with multiple exhibitor booths'
},
{
  id: 'case_4',
  title: 'Manufacturing Export Expansion',
  client: 'Asian Manufacturing Group',
  industry: 'Manufacturing',
  region: 'EMEA',
  service: 'Market Entry',
  result: '25 new distributors, $3.5M pipeline',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10c956190-1767389917789.png",
  alt: 'Manufacturing equipment displayed at trade show'
},
{
  id: 'case_5',
  title: 'Real Estate Project Launch',
  client: 'International Real Estate Developer',
  industry: 'Real Estate',
  region: 'EMEA',
  service: 'Exhibition Sales',
  result: '120 qualified investors, 15 pre-sales',
  image: "https://images.unsplash.com/photo-1727041426536-5767189a30bc",
  alt: 'Real estate exhibition booth with architectural models'
},
{
  id: 'case_6',
  title: 'Healthcare Innovation Showcase',
  client: 'European MedTech Startup',
  industry: 'Healthcare',
  region: 'EMEA',
  service: 'Consulting',
  result: '35 meetings with decision-makers, 6 pilot programs',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e5be503e-1764659715073.png",
  alt: 'Healthcare technology exhibition with medical devices'
},
{
  id: 'case_7',
  title: 'Food & Beverage Export Drive',
  client: 'Mediterranean Food Exporter',
  industry: 'Food & Beverage',
  region: 'CIS',
  service: 'Representation',
  result: '18 distribution agreements, 8 countries',
  image: "https://images.unsplash.com/photo-1680345576216-5c0e8635153d",
  alt: 'Food and beverage products displayed at trade fair'
},
{
  id: 'case_8',
  title: 'Renewable Energy Partnerships',
  client: 'European Green Tech Firm',
  industry: 'Energy',
  region: 'EMEA',
  service: 'Market Entry',
  result: '22 strategic partnerships, 4 government contracts',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b1dcb5af-1767814820964.png",
  alt: 'Renewable energy technology at exhibition'
},
{
  id: 'case_9',
  title: 'Logistics Solution Expansion',
  client: 'Asian Logistics Provider',
  industry: 'Logistics',
  region: 'EMEA',
  service: 'Exhibition Sales',
  result: '42 qualified leads, 12 long-term contracts',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f844f33c-1767533843315.png",
  alt: 'Logistics and supply chain exhibition booth'
}];


type FilterType = 'all' | 'industry' | 'region' | 'service';

export default function CaseStudyGrid() {
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [selectedValue, setSelectedValue] = useState<string>('all');

  const filters = {
    industry: ['Technology', 'Finance', 'Manufacturing', 'Real Estate', 'Healthcare', 'Food & Beverage', 'Energy', 'Logistics'],
    region: ['EMEA', 'CIS'],
    service: ['Exhibition Sales', 'Market Entry', 'Representation', 'Consulting']
  };

  const filteredCases =
  selectedValue === 'all' ?
  allCaseStudies :
  allCaseStudies.filter((caseStudy) => {
    if (activeFilter === 'industry') return caseStudy.industry === selectedValue;
    if (activeFilter === 'region') return caseStudy.region === selectedValue;
    if (activeFilter === 'service') return caseStudy.service === selectedValue;
    return true;
  });

  const handleFilterChange = (filterType: FilterType, value: string) => {
    setActiveFilter(filterType);
    setSelectedValue(value);
  };

  return (
    <section className="section-padding bg-background">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-12 animate-fadeIn">
          <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              Full Portfolio
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground">
            All Case Studies
          </h2>
        </div>

        {/* Filters */}
        <div className="mb-12 space-y-4">
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => handleFilterChange('all', 'all')}
              className={`px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all ${
              selectedValue === 'all' ? 'bg-primary text-primary-foreground shadow-lg' : 'bg-muted text-muted-foreground hover:bg-primary/20'}`
              }>

              All Cases
            </button>

            {Object.entries(filters).map(([filterType, values]) =>
            <div key={filterType} className="relative group">
                <button className="px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider bg-muted text-muted-foreground hover:bg-primary/20 transition-all flex items-center gap-2">
                  {filterType}
                  <Icon name="ChevronDownIcon" size={16} />
                </button>

                {/* Dropdown */}
                <div className="absolute top-full left-0 mt-2 bg-card rounded-xl shadow-2xl border border-border p-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                  {values.map((value) =>
                <button
                  key={value}
                  onClick={() => handleFilterChange(filterType as FilterType, value)}
                  className="w-full text-left px-4 py-2 rounded-lg text-sm font-medium hover:bg-muted transition-colors">

                      {value}
                    </button>
                )}
                </div>
              </div>
            )}
          </div>

          {selectedValue !== 'all' &&
          <div className="text-center">
              <span className="text-sm text-muted-foreground">
                Showing <strong className="text-foreground">{filteredCases.length}</strong> results for:{' '}
                <strong className="text-primary">{selectedValue}</strong>
              </span>
            </div>
          }
        </div>

        {/* Case Study Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCases.map((caseStudy, index) =>
          <div
            key={caseStudy.id}
            className="card-base card-hover overflow-hidden group animate-fadeIn"
            style={{ animationDelay: `${index * 0.05}s` }}>

              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <AppImage
                src={caseStudy.image}
                alt={caseStudy.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute top-4 left-4 bg-accent/90 backdrop-blur-sm px-3 py-1 rounded-full">
                  <span className="text-xs font-bold uppercase tracking-wider text-white">
                    {caseStudy.service}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                  {caseStudy.title}
                </h3>

                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    <strong className="text-foreground">Client:</strong> {caseStudy.client}
                  </p>
                  <p>
                    <strong className="text-foreground">Industry:</strong> {caseStudy.industry}
                  </p>
                  <p>
                    <strong className="text-foreground">Region:</strong> {caseStudy.region}
                  </p>
                </div>

                <div className="pt-4 border-t border-border">
                  <p className="text-sm font-bold text-accent">{caseStudy.result}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {filteredCases.length === 0 &&
        <div className="text-center py-16">
            <Icon name="FolderOpenIcon" size={64} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-xl text-muted-foreground">No case studies found for this filter.</p>
          </div>
        }
      </div>
    </section>);

}